"use client";

import { cn } from "@/lib/utils";
import React, { useState } from "react";
import { Button } from "../ui/button";
import { Check, Loader2 } from "lucide-react";
import { BillingInterval, Plan, Subscription } from "@prisma/client";
import { useQuery } from "@tanstack/react-query";
import LoadingStatus from "../ui/loading-status";
import { useAuth } from "@/hooks/useAuth";

const stripEmoji = (text: string) =>
  text.replace(/\p{Emoji_Presentation}|\p{Extended_Pictographic}/gu, "").replace(/\s+/g, " ").trim();

// Same Studio in both plans — no story limits, no credit anxiety, fully refundable.
// Shown identically on both cards; only price/interval differ between them.
const SHARED_FEATURES = [
  "AI story outline generator",
  "Story structures, 8 to choose from",
  "Story Analyzer, re-run whenever you revise",
  "Manuscript editor with version history",
  "AI writing assistant that knows your book",
  "Character profiles, arcs and relationships",
  "Story map, timeline and Story Bible",
  "Export to .doc, .pdf, .txt, .md and .html",
  "Word counts, goals and a writing streak",
  "Unlimited stories",
  "Auto-save on every device with a browser",
  "Every new feature we release",
];

// Extended subscription type that includes the plan relation
type SubscriptionWithPlan = Subscription & {
  plan: Plan | null;
};

function PricingPlans({
  isLandingPage = false,
  initialPlans,
}: {
  isLandingPage?: boolean;
  /** Plans rendered on the server, so prices are in the page's HTML (search engines, no-JS). */
  initialPlans?: Plan[];
}) {
  const { session, showLoginModal, openLoginModal, LoginModalComponent } = useAuth();
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null);

  // Fetch plans
  const { data: plans, isLoading: plansLoading } = useQuery<Plan[]>({
    queryKey: ["subscription-plans"],
    queryFn: async () => {
      const res = await fetch("/api/subscriptions/plans");
      if (!res.ok) throw new Error("Failed to fetch plans");
      return res.json();
    },
    initialData: initialPlans?.length ? initialPlans : undefined,
  });

  // Fetch current subscription if user is logged in
  const { data: subscription, isLoading: subscriptionLoading } =
    useQuery<SubscriptionWithPlan>({
      queryKey: ["subscription"],
      queryFn: async () => {
        const res = await fetch("/api/subscriptions/current", {
          cache: "no-store",
          credentials: "include",
        });
        if (!res.ok) throw new Error("Failed to fetch subscription");
        return res.json();
      },
      enabled: !!session, // Only fetch if user is logged in
    });

  const handlePlanAction = async (planId: string) => {
    if (!session) {
      openLoginModal();
      return;
    }

    try {
      setLoadingPlanId(planId);
      
      // Call checkout API
      const response = await fetch("/api/subscriptions/checkout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ planId }),
      });

      if (!response.ok) {
        throw new Error("Failed to create checkout session");
      }

      const data = await response.json();
      
      // Redirect to Creem checkout
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      alert("Failed to start checkout. Please try again.");
    } finally {
      setLoadingPlanId(null);
    }
  };

  const isLoading = plansLoading || subscriptionLoading;

  // Get monthly and yearly plans
  const monthlyPlan = plans?.find(
    (plan) => plan.interval === BillingInterval.MONTHLY
  );
  const yearlyPlan = plans?.find(
    (plan) => plan.interval === BillingInterval.YEARLY
  );

  const calculateYearlySavings = () => {
    if (!monthlyPlan || !yearlyPlan) return 0;
    const monthlyYearlyTotal = monthlyPlan.price * 12;
    const savings = monthlyYearlyTotal - yearlyPlan.price;
    const percentage = Math.round((savings / monthlyYearlyTotal) * 100);
    return percentage;
  };

  // Check if a plan is the user's current plan by comparing variantId
  const isCurrentPlan = (plan: Plan) => {
    console.log({ subscription });
    if (!subscription || !subscription.plan) return false;
    return (
      subscription.plan.variantId === plan.variantId &&
      subscription.status !== "CANCELLED"
    );
  };

  // Check if user can switch to a plan (only monthly to yearly allowed)
  const canSwitchToPlan = (plan: Plan) => {
    if (!session) return true; // Show normal button for non-logged users
    if (!subscription) return true; // No subscription, can subscribe

    // If user has an active subscription
    if (subscription.plan) {
      // If trying to switch to monthly plan and user is on yearly - not allowed
      if (
        plan.interval === BillingInterval.MONTHLY &&
        subscription.plan.interval === BillingInterval.YEARLY
      ) {
        return false;
      }

      // If trying to switch to yearly plan and user is on monthly - allowed
      if (
        plan.interval === BillingInterval.YEARLY &&
        subscription.plan.interval === BillingInterval.MONTHLY
      ) {
        return true;
      }
    }

    return !isCurrentPlan(plan); // Can switch if not current plan
  };

  const getButtonText = (plan: Plan) => {
    if (loadingPlanId === plan.id) {
      return (
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading...
        </div>
      );
    }

    if (!session) {
      return "Get Started";
    }

    if (isCurrentPlan(plan)) {
      return "Current Plan";
    }

    // If user cannot switch to this plan (yearly to monthly)
    if (!canSwitchToPlan(plan)) {
      return "Not Available";
    }

    if (!subscription) {
      return `Start ${plan.interval === BillingInterval.MONTHLY ? "Monthly" : "Yearly"} Plan`;
    }

    return `Switch to ${plan.interval === BillingInterval.MONTHLY ? "Monthly" : "Yearly"}`;
  };

  const getButtonVariant = (plan: Plan) => {
    if (isCurrentPlan(plan)) {
      return "outline";
    }
    return "default";
  };

  const isButtonDisabled = (plan: Plan) => {
    return (
      loadingPlanId === plan.id || isCurrentPlan(plan) || !canSwitchToPlan(plan)
    );
  };

  return (
    <section
      id="pricing"
      className={`mx-auto max-w-7xl pt-8 ${
        isLandingPage ? "px-4 py-14 sm:py-20" : "px-6 lg:px-8"
      } `}
    >
      {isLandingPage && (
        <div className="mx-auto max-w-4xl text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="block h-px w-5 bg-lp-mark" />
            <span className="text-[11px] font-medium text-lp-ink-3 tracking-[0.18em] uppercase">Pricing that&apos;s worth every dollar you spend</span>
            <span className="block h-px w-5 bg-lp-mark" />
          </div>
          <h2 className="lp-h2 text-center mb-5">
            No writer should go broke
            <br />
            <span className="lp-sub">finishing their book.</span>
          </h2>
          <p className="text-[16px] text-lp-ink-2 max-w-xl mx-auto">
            One plan. Everything in it. No starter tier with the good parts removed, no credits anxiety, no usage meter. The only choice you make is whether this tool is worth your investment, and how long you&apos;re paying for.
          </p>
        </div>
      )}

      {isLoading ? (
        <LoadingStatus />
      ) : (
        <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {/* Monthly Plan */}
          {monthlyPlan && (
            <div className="relative">
              <div
                className={cn(
                  "rounded-2xl p-8 border border-lp-rule bg-lp-paper transition-colors duration-200",
                  isCurrentPlan(monthlyPlan) && "border-lp-action",
                  !canSwitchToPlan(monthlyPlan) &&
                    !isCurrentPlan(monthlyPlan) &&
                    "opacity-50 cursor-not-allowed"
                )}
              >
                <div className="mb-6">
                  <p className="text-[11px] font-medium text-lp-ink-3 tracking-[0.18em] uppercase mb-3">{monthlyPlan.name}</p>
                  <div className="flex items-end gap-1 mb-2">
                    <span className="font-blog text-5xl font-medium tracking-tight text-lp-ink">${monthlyPlan.price}</span>
                    <span className="text-[14px] text-lp-ink-3 mb-1">/month</span>
                  </div>
                  <p className="text-[13px] text-lp-ink-2">
                    Flexible billing, cancel anytime.
                  </p>
                </div>

                <Button
                  className={cn(
                    "w-full mb-6",
                    !isButtonDisabled(monthlyPlan) && "lp-btn-secondary"
                  )}
                  onClick={() => handlePlanAction(monthlyPlan.id)}
                  disabled={isButtonDisabled(monthlyPlan)}
                  variant={getButtonVariant(monthlyPlan)}
                >
                  {getButtonText(monthlyPlan)}
                </Button>

                {subscription?.plan?.interval === BillingInterval.YEARLY &&
                  monthlyPlan.interval === BillingInterval.MONTHLY && (
                    <div className="mb-4">
                      <p className="text-[12px] text-lp-ink-3">
                        Downgrade to monthly is not available. Your yearly plan continues until renewal.
                      </p>
                    </div>
                  )}

                <div className="border-t border-lp-rule pt-6">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-lp-ink-3 mb-3">
                    Same Studio in both plans
                  </p>
                  <ul className="space-y-3">
                    {SHARED_FEATURES.map((feature) => (
                      <li key={feature} className="flex items-start gap-x-3">
                        <Check className="h-4 w-4 flex-none text-lp-mark mt-0.5" />
                        <span className="text-[14px] text-lp-ink-2">{stripEmoji(feature)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Yearly Plan */}
          {yearlyPlan && (
            <div className="relative">
              {/* Savings badge */}
              <div className="absolute -top-3.5 left-6 z-10">
                <div className="flex items-center gap-2 bg-lp-ink text-lp-paper px-3 py-1 rounded-full">
                  <span className="text-[11px] font-semibold tracking-wide">Save {calculateYearlySavings()}%</span>
                </div>
              </div>

              <div
                className={cn(
                  "rounded-2xl p-8 border-2 border-lp-rule-strong bg-lp-paper transition-colors duration-200",
                  isCurrentPlan(yearlyPlan) && "border-lp-action",
                  !canSwitchToPlan(yearlyPlan) &&
                    !isCurrentPlan(yearlyPlan) &&
                    "opacity-50 cursor-not-allowed"
                )}
              >
                <div className="mb-6">
                  <p className="text-[11px] font-medium text-lp-ink-3 tracking-[0.18em] uppercase mb-3">{yearlyPlan.name}</p>
                  <div className="flex items-end gap-1 mb-2">
                    <span className="font-blog text-5xl font-medium tracking-tight text-lp-ink">${yearlyPlan.price}</span>
                    <span className="text-[14px] text-lp-ink-3 mb-1">/year</span>
                  </div>
                  {monthlyPlan && (
                    <div className="flex items-center gap-2">
                      <span className="text-[12px] text-lp-ink-3 line-through">${monthlyPlan.price * 12}/year</span>
                      <span className="text-[12px] font-semibold text-lp-mark">
                        Save ${monthlyPlan.price * 12 - yearlyPlan.price}
                      </span>
                    </div>
                  )}
                </div>

                <Button
                  className={cn(
                    "w-full mb-6",
                    !isButtonDisabled(yearlyPlan) && "lp-btn"
                  )}
                  onClick={() => handlePlanAction(yearlyPlan.id)}
                  disabled={isButtonDisabled(yearlyPlan)}
                  variant={getButtonVariant(yearlyPlan)}
                >
                  {getButtonText(yearlyPlan)}
                </Button>

                <div className="border-t border-lp-rule pt-6">
                  <p className="text-[11px] font-semibold uppercase tracking-wide text-lp-ink-3 mb-3">
                    Same Studio in both plans
                  </p>
                  <ul className="space-y-3">
                    {SHARED_FEATURES.map((feature) => (
                      <li key={feature} className="flex items-start gap-x-3">
                        <Check className="h-4 w-4 flex-none text-lp-mark mt-0.5" />
                        <span className="text-[14px] text-lp-ink-2">{stripEmoji(feature)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Trust indicators */}
      {isLandingPage && (
        <div className="mt-10 text-center">
          <div className="flex flex-wrap justify-center items-center gap-6">
            {["Cancel anytime", "Instant access", "No setup fees"].map((t) => (
              <div key={t} className="flex items-center gap-2">
                <Check className="h-3.5 w-3.5 text-lp-mark" />
                <span className="text-[13px] text-lp-ink-3">{t}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showLoginModal && LoginModalComponent}
    </section>
  );
}

export default PricingPlans;
